import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, PauseCircle, RotateCcw } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', pending_approval: 'Pending Approval', returned: 'Returned for Redraft',
  rejected: 'Rejected', active: 'Active', closed: 'Closed', suspended: 'Suspended',
};

export function GrantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const grantId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.grants.manage');

  const grantQuery = useQuery({ queryKey: ['grant', grantId], queryFn: () => financeApi.getGrant(grantId) });
  const docsQuery = useQuery({ queryKey: ['grant-documents', grantId], queryFn: () => genericDocumentApi.listFor('grant', grantId) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['grant', grantId] });

  const toggleStatusMutation = useMutation({
    mutationFn: (status: 'active' | 'closed' | 'suspended') => financeApi.setGrantStatus(grantId, status),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const submitMutation = useMutation({
    mutationFn: () => financeApi.submitGrant(grantId),
    onSuccess: () => { showSuccess('Submitted for approval.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit.'),
  });

  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => financeApi.decideGrant(grantId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  if (!grantQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const g = grantQuery.data.data;

  return (
    <div className="page">
      <Link to="/finance/grants">&larr; Back to Grants</Link>
      <RecordHeader
        title={`Grant - ${g.grant_reference}`}
        entityType="grant"
        entityId={grantId}
        fields={[
          { label: 'Donor', value: g.donor_name ?? '-' },
          { label: 'Project', value: g.project_name ?? '-' },
          { label: 'Grant number', value: g.grant_number ?? '-' },
          { label: 'Period', value: `${formatDate(g.start_date)} - ${formatDate(g.end_date)}` },
          { label: 'Total award amount', value: g.total_award_amount != null ? `${g.currency_code} ${Number(g.total_award_amount).toLocaleString()}` : '-' },
          { label: 'Status', value: <StatusBadge status={g.status} label={STATUS_LABELS[g.status]} /> },
        ]}
        actions={
          <span className="icon-action-group">
            {['draft', 'returned'].includes(g.status) && (
              <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>{submitMutation.isPending ? 'Submitting…' : 'Submit for Approval'}</button>
            )}
            {g.status === 'pending_approval' && (
              <>
                <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}>Approve</button>
                <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}>Reject</button>
                <ReturnAction onReturn={(comment) => financeApi.returnGrantForRedraft(grantId, comment)} queryKeysToInvalidate={[['grant', grantId]]} />
              </>
            )}
            {g.status === 'active' && <button className="btn-secondary" onClick={() => toggleStatusMutation.mutate('closed')}><Lock size={16} className="inline-icon" /> Close</button>}
            {g.status === 'active' && <button className="btn-secondary" onClick={() => toggleStatusMutation.mutate('suspended')}><PauseCircle size={16} className="inline-icon" /> Suspend</button>}
            {['closed', 'suspended'].includes(g.status) && <button className="btn-primary" onClick={() => toggleStatusMutation.mutate('active')}><RotateCcw size={16} className="inline-icon" /> Reactivate</button>}
          </span>
        }
        readOnly={!canManage}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: <CommentsThread entityType="grant" entityId={grantId} />,
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('grant', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('grant', grantId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('grant', documentId)}
                queryKeyToInvalidate={['grant-documents', grantId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="grant" entityId={grantId} statusLabels={STATUS_LABELS} />,
          },
        ]}
      />
    </div>
  );
}
